// contexts/DataContext.js
import React, { createContext, useContext, useState } from 'react';
import { FfttService } from '../services/FFTTService';
import { parseFrenchDate } from '../utils/utils';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  // État pour les joueurs
  const [joueurs, setJoueurs] = useState([]);
  const [joueursLoading, setJoueursLoading] = useState(false);
  const [joueursLoaded, setJoueursLoaded] = useState(false);
  const [joueursError, setJoueursError] = useState(null);

  // État pour les top joueurs
  const [topJoueurs, setTopJoueurs] = useState([]);
  const [topJoueursLoading, setTopJoueursLoading] = useState(false);
  const [topJoueursLoaded, setTopJoueursLoaded] = useState(false);
  const [topJoueursError, setTopJoueursError] = useState(null);

  // État pour les équipes
  const [equipes, setEquipes] = useState([]);
  const [equipesLoading, setEquipesLoading] = useState(false);
  const [equipesLoaded, setEquipesLoaded] = useState(false);
  const [equipesError, setEquipesError] = useState(null);

  // État pour les équipes pour les prochains matchs
  const [equipesProchainMatchs, setEquipesProchainMatchs] = useState([]);


  // État pour les matchs
  const [matchs, setMatchs] = useState({});
  const [matchsLoading, setMatchsLoading] = useState(false);
  const [matchsLoaded, setMatchsLoaded] = useState(false);
  const [matchsError, setMatchsError] = useState(null);

  // État pour les prochains matchs
  const [prochainsMatchs, setProchainsMatchs] = useState({});
  const [prochainsMatchsLoading, setProchainsMatchsLoading] = useState(false);
  const [prochainsMatchsLoaded, setProchainsMatchsLoaded] = useState(false);
  const [prochainsMatchsError, setProchainsMatchsError] = useState(null);

  // Fonction pour charger tous les joueurs
  const fetchJoueurs = async (force = false) => {
    if (joueursLoaded && !force) return;
    
    setJoueursLoading(true);
    setJoueursError(null);
    
    try {
      const ffttService = new FfttService();
      const data = await ffttService.fetchJoueursByPoints();
      setJoueurs(data);
      setJoueursLoaded(true);
    } catch (err) {
      setJoueursError(err.message);
    } finally {
      setJoueursLoading(false);
    }
  };

  // Fonction pour charger les top joueurs (les 3 premiers)
  const fetchTopJoueurs = async (force = false) => {
    if (topJoueursLoaded && !force) return;
    
    setTopJoueursLoading(true);
    setTopJoueursError(null);
    
    try {
      const ffttService = new FfttService();
      const data = await ffttService.fetchJoueursByPoints(true);
      setTopJoueurs(data);
      setTopJoueursLoaded(true);
    } catch (err) {
      setTopJoueursError(err.message);
    } finally {
      setTopJoueursLoading(false);
    }
  };

  // Fonction pour charger les équipes
  const fetchEquipes = async (force = false) => {
    if (equipesLoaded && !force) return;
    
    setEquipesLoading(true);
    setEquipesError(null);
    
    try {
      const ffttService = new FfttService();
      const data = await ffttService.fetchEquipes(false);
      setEquipes(data);
      setEquipesLoaded(true);
    } catch (err) {
      setEquipesError(err.message);
    } finally {
      setEquipesLoading(false);
    }
  };

  // Fonction pour charger les prochains matchs
  const fetchProchainsMatchs = async (force = false) => {
    if (prochainsMatchsLoaded && !force) return;
    
    setProchainsMatchsLoading(true);
    setProchainsMatchsError(null);
    
    try {
      const ffttService = new FfttService();
      const equipesData = await ffttService.fetchEquipes(true);
      setEquipesProchainMatchs(equipesData);

      const matchsData = {};
      for (const equipe of equipesData) {
        const matchs = await ffttService.fetchMatchs({
          cx_poule: equipe.liendivision.split('cx_poule=')[1].split('&')[0],
          D1: equipe.liendivision.split('D1=')[1].split('&')[0]
        });

        const now = new Date();
        const prochainMatch = matchs
          .filter(match => {
            const equa = match.equa ?? '';
            const equb = match.equb ?? '';
            let equipeLib = equipe.libequipe.split("-")[0].slice(0, -1);
            const dateMatch = parseFrenchDate(match.dateprevue);
            const nouvelleDate = new Date(dateMatch);
            nouvelleDate.setDate(nouvelleDate.getDate() - 3);

            return nouvelleDate >= now && (equa === equipeLib || equb === equipeLib);
          })
          .sort((a, b) => parseFrenchDate(a.dateprevue) - parseFrenchDate(b.dateprevue))[0];

        matchsData[equipe.idequipe] = prochainMatch;
      }

      setProchainsMatchs(matchsData);
      setProchainsMatchsLoaded(true);
    } catch (err) {
      setProchainsMatchsError(err.message);
    } finally {
      setProchainsMatchsLoading(false);
    }
  };

  // Fonction pour charger les matchs d'une équipe
  const fetchMatchsForEquipe = async (equipe) => {
    const key = equipe.liendivision;
    if (matchs[key]) return; // Déjà chargé
    
    setMatchsLoading(true);
    setMatchsError(null);
    
    try {
      const ffttService = new FfttService();
      const matchsData = await ffttService.fetchMatchs({
        cx_poule: equipe.liendivision.split('cx_poule=')[1].split('&')[0],
        D1: equipe.liendivision.split('D1=')[1].split('&')[0]
      });

      const filtreMatch = matchsData.filter(match => {
        const equa = match.equa ?? '';
        const equb = match.equb ?? '';
        let equipeLib = equipe.libequipe.split("-")[0].slice(0, -1);
        return equa === equipeLib || equb === equipeLib;
      });

      setMatchs(prev => ({
        ...prev,
        [key]: filtreMatch
      }));
      setMatchsLoaded(true);
    } catch (err) {
      setMatchsError(err.message);
    } finally {
      setMatchsLoading(false);
    }
  };

  // Fonction pour charger tous les matchs de toutes les équipes
  const fetchAllMatchs = async (equipesData, force = false) => {
    if (matchsLoaded && !force) return;
    
    setMatchsLoading(true);
    setMatchsError(null);
    
    try {
      const ffttService = new FfttService();
      const matchsData = {};
      
      for (const equipe of equipesData) {
        const matchsEquipe = await ffttService.fetchMatchs({
          cx_poule: equipe.liendivision.split('cx_poule=')[1].split('&')[0],
          D1: equipe.liendivision.split('D1=')[1].split('&')[0]
        });

        const filtreMatch = matchsEquipe.filter(match => {
          const equa = match.equa ?? '';
          const equb = match.equb ?? '';
          let equipeLib = equipe.libequipe.split("-")[0].slice(0, -1);
          return equa === equipeLib || equb === equipeLib;
        });

        matchsData[equipe.liendivision] = filtreMatch;
      }

      setMatchs(matchsData);
      setMatchsLoaded(true);
    } catch (err) {
      setMatchsError(err.message);
    } finally {
      setMatchsLoading(false);
    }
  };

  const value = {
    // Joueurs
    joueurs,
    joueursLoading,
    joueursError,
    fetchJoueurs,
    
    // Top Joueurs
    topJoueurs,
    topJoueursLoading,
    topJoueursError,
    fetchTopJoueurs,
    
    // Équipes
    equipes,
    equipesLoading,
    equipesError,
    fetchEquipes,

    
    // Matchs
    matchs,
    matchsLoading,
    matchsError,
    fetchMatchsForEquipe,
    fetchAllMatchs,

    // Prochains Matchs
    prochainsMatchs,
    prochainsMatchsLoading,
    prochainsMatchsError,
    equipesProchainMatchs,
    fetchProchainsMatchs
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};